import React from "react";
import { useNavigate } from "react-router-dom";
import Avatar from "../../../../components/Avatar/Avatar";
import { Lobby } from "../../../../services/api/LobbyApi";
import styles from "./PlayerList.module.scss";

interface PlayerListProps {
  lobby: Lobby;
  isHost?: boolean;
  onKick?: (userId: string) => void;
  onTransferHost?: (userId: string) => void;
}

function getRelativeTime(dateString: string): string {
  const now = Date.now();
  const joinedAt = new Date(dateString).getTime();
  const diffMs = now - joinedAt;
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  return `${diffHours}h ago`;
}

const PlayerList: React.FC<PlayerListProps> = ({ lobby, isHost, onKick, onTransferHost }) => {
  const navigate = useNavigate();

  const sortedMembers = [...lobby.members].sort((a, b) => {
    if (a.userId === lobby.hostId) return -1;
    if (b.userId === lobby.hostId) return 1;
    return new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime();
  });

  const emptySlots = lobby.maxPlayers - lobby.members.length;

  return (
    <div className={styles.playerList}>
      <div className={styles.gameInfo}>
        {lobby.preferences.gameMode && <span className={styles.tag}>{lobby.preferences.gameMode}</span>}
        {lobby.preferences.region && <span className={styles.tag}>{lobby.preferences.region}</span>}
        {lobby.preferences.skillLevel && (
          <span className={`${styles.tag} ${styles.skillTag}`}>{lobby.preferences.skillLevel}</span>
        )}
        {lobby.preferences.voiceRequired && (
          <span className={`${styles.tag} ${styles.voiceTag}`}>Voice Required</span>
        )}
        <span className={styles.tag}>{lobby.members.length}/{lobby.maxPlayers} players</span>
      </div>

      <div className={styles.members}>
        {sortedMembers.map((member) => {
          const memberIsHost = member.userId === lobby.hostId;
          return (
            <div
              key={member.userId}
              className={`${styles.member} ${memberIsHost ? styles.hostMember : ""}`}
            >
              <Avatar
                src={member.avatarUrl}
                username={member.username}
                size={memberIsHost ? 44 : 34}
                onClick={() => navigate(`/profile?user=${member.userId}`)}
              />
              <div className={styles.memberInfo}>
                <span className={styles.username}>
                  {member.username}
                  {memberIsHost && <span className={styles.hostBadge}>Host</span>}
                </span>
                <span className={styles.joinedAt}>joined {getRelativeTime(member.joinedAt)}</span>
              </div>
              {isHost && !memberIsHost && (
                <div className={styles.memberActions}>
                  <button className={styles.actionBtn} onClick={() => onTransferHost?.(member.userId)}>
                    Make Host
                  </button>
                  <button className={`${styles.actionBtn} ${styles.kickBtn}`} onClick={() => onKick?.(member.userId)}>
                    Kick
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {Array.from({ length: emptySlots }).map((_, i) => (
          <div key={`empty-${i}`} className={styles.emptySlot}>
            <div className={styles.emptyAvatar} />
            <span className={styles.emptyLabel}>Waiting for player...</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlayerList;
